// ECharts renders to canvas and cannot read CSS custom properties, so chart colors are literals here.
import { DEFAULT_THEME } from '@mantine/core'
import { theme as dsTheme } from '@skie-io/react'

const datavizSeries = dsTheme.other.dataviz.series
const ds = dsTheme.colors
const mantine = DEFAULT_THEME.colors

// Tableau 20
// https://github.com/nagix/chartjs-plugin-colorschemes/blob/d96a01846626881aa4bec56828c333af81050906/src/colorschemes/colorschemes.tableau.js#L7
const tableau20 = [
  '#4E79A7',
  '#A0CBE8',
  '#F28E2B',
  '#FFBE7D',
  '#59A14F',
  '#8CD17D',
  '#B6992D',
  '#F1CE63',
  '#499894',
  '#86BCB6',
  '#E15759',
  '#FF9D9A',
  '#79706E',
  '#BAB0AC',
  '#D37295',
  '#FABFD2',
  '#B07AA1',
  '#D4A6C8',
  '#9D7660',
  '#D7B5A6',
]

const colors = [
  '#4E79A7',
  '#F28E2B',
  '#E15759',
  '#76B7B2',
  '#59A14F',
  '#EDC948',
  '#B07AA1',
  '#FF9DA7',
  '#9C755F',
  '#BAB0AC',
  '#1F77B4',
  '#FF7F0E',
  '#2CA02C',
  '#D62728',
  '#9467BD',
  '#8C564B',
  '#E377C2',
  '#7F7F7F',
  '#BCBD22',
  '#17BECF',
  '#393B79',
  '#5254A3',
  '#6B6ECF',
  '#9C9EDE',
  '#637939',
  '#8CA252',
  '#B5CF6B',
  '#CEDB9C',
  '#8C6D31',
  '#BD9E39',
  '#E7BA52',
  '#E7CB94',
  '#843C39',
  '#AD494A',
  '#D6616B',
  '#E7969C',
  '#7B4173',
  '#A55194',
  '#CE6DBD',
  '#DE9ED6',
  '#3182BD',
  '#6BAED6',
  '#9ECAE1',
  '#C6DBEF',
  '#E6550D',
  '#FD8D3C',
  '#FDAE6B',
  '#FDD0A2',
  '#31A354',
  '#74C476',
  '#A1D99B',
  '#C7E9C0',
  '#756BB1',
  '#9E9AC8',
  '#BCBDDC',
  '#DADAEB',
  '#636363',
  '#969696',
  '#BDBDBD',
  '#D9D9D9',
]

const darkSeriesReplacements = {
  [ds.skieDelft[9]]: ds.skieDelft[1],
  [ds.red[6]]: ds.red[4],
  '#393B79': ds.indigo[4],
  '#5254A3': mantine.indigo[6],
  '#843C39': mantine.red[8],
  '#AD494A': mantine.red[6],
  '#7B4173': mantine.grape[7],
  '#636363': mantine.dark[2],
  '#8C564B': '#98675D',
}

const forDark = (palette) => palette.map((color) => darkSeriesReplacements[color] ?? color)

const palettes = {
  light: { small: datavizSeries, medium: tableau20, large: colors },
  dark: { small: forDark(datavizSeries), medium: forDark(tableau20), large: forDark(colors) },
}

const palettesFor = (scheme) => palettes[scheme === 'dark' ? 'dark' : 'light']

export const seriesPalette = (scheme) => palettesFor(scheme).small

export function colorScheme(data, scheme) {
  const { small, medium, large } = palettesFor(scheme)
  if (data.length > 20) {
    return large
  }
  return data.length <= small.length ? small : medium
}

export const complianceFallbackColor = tableau20[13]

export const ghostFillNeutral = 'rgba(176, 200, 220, 0.14)'
export const ghostFillNegative = 'rgba(232, 122, 95, 0.14)'

// Create explicit color mapping for compliance states to match stacked bar chart with pie chart
export const complianceColors = {
  Compliant: '#59A14F',
  'Critical Noncompliant': '#E15759',
  'Security Noncompliant': '#9467BD',
  'Other Noncompliant': '#F28E2B',
}

// Bootstrap/Tabler severity colors — matches severity_badge helper in Ruby
export const severityColors = {
  Critical: '#d63939',
  High: '#f76707',
  Medium: '#f59f00',
  Low: '#066fd1',
  Informational: '#9ca3af',
}

export const severityColorsDark = {
  ...severityColors,
  Low: '#4778F9',
}

const chrome = {
  light: {
    text: '#333333',
    mutedText: ds.skieDelft[5],
    axisLine: '#666666',
    nodeBorder: '#666666',
    labelBg: 'rgba(255, 255, 255, 0.8)',
    labelText: '#1a1a1a',
    trendLine: '#374151',
    cellBorder: dsTheme.white,
    heatmap: ['#f3f4f6', '#fef3c7', '#fed7aa', '#fecaca', '#ef4444'],
    heatmapLabel: { strong: '#111827', normal: '#1f2937', faint: '#6b7280' },
    heatmapEmphasisBorder: '#3b82f6',
    heatmapEmphasisShadow: 'rgba(59,130,246,0.3)',
  },
  dark: {
    text: ds.dark[0],
    mutedText: ds.dark[1],
    axisLine: ds.dark[2],
    nodeBorder: ds.dark[2],
    labelBg: 'rgba(14, 24, 35, 0.85)',
    labelText: ds.dark[0],
    trendLine: ds.dark[1],
    cellBorder: ds.dark[7],
    heatmap: [ds.dark[5], '#5C4A1A', '#7A3E12', '#7F1D1D', '#EF4444'],
    heatmapLabel: { strong: ds.dark[7], normal: ds.dark[0], faint: ds.dark[2] },
    heatmapEmphasisBorder: '#3b82f6',
    heatmapEmphasisShadow: 'rgba(59,130,246,0.3)',
  },
}

export const chartChrome = (scheme) => chrome[scheme === 'dark' ? 'dark' : 'light']
